import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { EyeRegular } from "@fluentui/react-icons";
import "../styles/citemed-theme.css";

const STATE_META = {
    I: { label: 'Included', bg: '#DCFCE7', fg: '#166534' },
    M: { label: 'Maybe', bg: '#FEF3C7', fg: '#92400E' },
    E: { label: 'Excluded', bg: '#FEE2E2', fg: '#991B1B' },
    U: { label: 'Unclassified', bg: '#E5E7EB', fg: '#374151' },
    D: { label: 'Duplicate', bg: '#E0E7FF', fg: '#3730A3' },
};

export const CitationItem = ({ id, title, publication_year, authors, state, tags = [], selected, onClickItem, onCheckItem }) => {
    const authorsList = authors ? `${authors}, ` : "";
    const isSelected = selected.includes(id);
    const titleRef = useRef(null);

    const styles = {
        listItem: {
            padding: '8px',
            marginBottom: '4px',
            background: isSelected ? 'var(--Primary-5)' : 'white',
            border: `1px solid ${isSelected ? 'var(--Primary-500)' : 'var(--Gray-50)'}`,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'flex-start',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        },
        checkbox: {
            marginTop: '4px',
            marginRight: '8px',
            width: '14px',
            height: '14px',
            borderRadius: '3px',
            border: `1px solid ${isSelected ? 'var(--Primary-600)' : 'var(--Gray-300)'}`,
            backgroundColor: isSelected ? 'var(--Primary-600)' : 'white',
            cursor: 'pointer',
            flexShrink: 0,
        },
        labelContainer: {
            flex: 1,
            overflow: 'hidden',
        },
        title: {
            margin: '0 0 2px 0',
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: '18px',
            color: 'var(--Gray-900)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
        },
        readMoreButton: {
            background: 'none',
            border: 'none',
            color: 'var(--Primary-600)',
            cursor: 'pointer',
            padding: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
        },
        authors: {
            fontSize: '12px',
            lineHeight: '16px',
            color: 'var(--Gray-600)',
            marginBottom: '2px',
        },
        year: {
            display: 'inline-block',
            padding: '1px 4px',
            fontSize: '10px',
            fontWeight: 500,
            backgroundColor: 'var(--Gray-50)',
            color: 'var(--Gray-700)',
            borderRadius: '3px',
            marginRight: '8px',
        },
        yearContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
        },
        stateBadge: {
            display: 'inline-block',
            padding: '1px 6px',
            fontSize: '10px',
            fontWeight: 600,
            borderRadius: '3px',
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
        },
        tagChip: {
            display: 'inline-flex',
            alignItems: 'center',
            padding: '1px 6px',
            fontSize: '10px',
            fontWeight: 500,
            color: 'var(--Gray-800)',
            borderRadius: '10px',
            border: '1px solid var(--Gray-200)',
            maxWidth: '100px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    };

    const stateMeta = state ? STATE_META[state] : null;

    const handleMouseEnter = (e) => {
        if (!isSelected) {
            e.currentTarget.style.borderColor = 'var(--Primary-200)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(53, 120, 255, 0.08)';
        }
    };

    const handleMouseLeave = (e) => {
        if (!isSelected) {
            e.currentTarget.style.borderColor = 'var(--Gray-50)';
            e.currentTarget.style.boxShadow = 'none';
        }
    };

    const handleItemClick = () => {
        onCheckItem(id);
    };

    const handleReadMoreClick = (e) => {
        e.stopPropagation();
        onClickItem();
    };

    return (
        <li
            style={styles.listItem}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleItemClick}
        >
            <input
                type="checkbox"
                style={styles.checkbox}
                checked={isSelected}
                onChange={() => onCheckItem(id)}
                onClick={(e) => e.stopPropagation()}
            />
            <div style={styles.labelContainer}>
                <p ref={titleRef} style={styles.title}>{title}</p>
                {authorsList && <div style={styles.authors}>{authorsList}</div>}
                <div style={styles.yearContainer}>
                    {publication_year && <span style={styles.year}>{publication_year}</span>}
                    {stateMeta && (
                        <span
                            style={{ ...styles.stateBadge, backgroundColor: stateMeta.bg, color: stateMeta.fg }}
                            title={`Inclusion status: ${stateMeta.label}`}
                        >
                            {stateMeta.label}
                        </span>
                    )}
                    {tags.map(tag => (
                        <span
                            key={tag.id}
                            style={{ ...styles.tagChip, backgroundColor: tag.color || 'white' }}
                            title={tag.name}
                        >
                            {tag.name}
                        </span>
                    ))}
                    <button
                        className="read-more-btn"
                        style={styles.readMoreButton}
                        onClick={handleReadMoreClick}
                        title="View details"
                    >
                        <EyeRegular fontSize={18} />
                    </button>
                </div>
            </div>
        </li>
    );
};
